import { useState, useEffect } from "react";
import {
  Calendar, Play, Loader2, BarChart3,
  Eye, EyeOff, FileSpreadsheet, Factory
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import EnhancedGantt from "@/components/EnhancedGantt";
import WhatIfPanel from "@/components/WhatIfPanel";
import { downloadExcel } from "@/utils/excelExport";
import ScheduleSummaryCards from "@/components/scheduling/ScheduleSummaryCards";
import OrdersTable from "@/components/scheduling/OrdersTable";
import CapacityView from "@/components/scheduling/CapacityView";
import WorkCentersGrid from "@/components/scheduling/WorkCentersGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Scheduling = () => {
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [resources, setResources] = useState([]);
  const [viewMode, setViewMode] = useState("Day");
  const [optimizeFor, setOptimizeFor] = useState("on_time");
  const [activeTab, setActiveTab] = useState("gantt");
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [showWhatIfGantt, setShowWhatIfGantt] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [creatingPO, setCreatingPO] = useState({});

  useEffect(() => { fetchResources(); }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error("Failed to fetch resources:", error);
    }
  };

  const generateSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/schedule/from-sales-orders?optimize_for=${optimizeFor}`,
        {},
        { timeout: 120000 }
      );
      setScheduleData(response.data);
      setWhatIfResult(null);
      setShowWhatIfGantt(false);
      setSelectedOrders(new Set());
      if (response.data.total_orders === 0) {
        toast.info("No items found that need scheduling");
      } else {
        toast.success(`Scheduled ${response.data.total_orders} orders successfully`);
      }
    } catch (error) {
      toast.error(`Failed to generate schedule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createSinglePO = async (order) => {
    const orderId = order.order_id;
    setCreatingPO(prev => ({ ...prev, [orderId]: "loading" }));
    try {
      const resp = await axios.post(`${API}/production-orders/create`, {
        material: order.material,
        quantity: order.quantity,
        start_date: order.scheduled_start?.split('T')[0] || new Date().toISOString().split('T')[0],
        end_date: order.scheduled_end?.split('T')[0] || order.due_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      });
      if (resp.data.success) {
        setCreatingPO(prev => ({ ...prev, [orderId]: "success" }));
        toast.success(`PO ${resp.data.production_order} created for ${order.material}`);
      } else {
        setCreatingPO(prev => ({ ...prev, [orderId]: "error" }));
        toast.error(`Failed: ${resp.data.message}`);
      }
    } catch (error) {
      setCreatingPO(prev => ({ ...prev, [orderId]: "error" }));
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createBatchPO = async () => {
    const orders = scheduleData?.orders?.filter(o => selectedOrders.has(o.order_id)) || [];
    if (orders.length === 0) { toast.error("Select orders first"); return; }
    const loadingState = {};
    orders.forEach(o => { loadingState[o.order_id] = "loading"; });
    setCreatingPO(prev => ({ ...prev, ...loadingState }));
    try {
      const resp = await axios.post(`${API}/production-orders/create-batch`, {
        orders: orders.map(o => ({
          material: o.material, quantity: o.quantity,
          start_date: o.scheduled_start?.split('T')[0] || new Date().toISOString().split('T')[0],
          end_date: o.scheduled_end?.split('T')[0] || o.due_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        })),
      }, { timeout: 120000 });
      const results = resp.data.results || [];
      const newState = {};
      results.forEach((r, i) => {
        const oid = orders[i]?.order_id;
        if (oid) newState[oid] = r.success ? "success" : "error";
      });
      setCreatingPO(prev => ({ ...prev, ...newState }));
      toast.success(`Created ${resp.data.success}/${resp.data.total} production orders`);
      if (resp.data.failed > 0) toast.error(`${resp.data.failed} orders failed`);
    } catch (error) {
      orders.forEach(o => { setCreatingPO(prev => ({ ...prev, [o.order_id]: "error" })); });
      toast.error(`Batch creation failed: ${error.message}`);
    }
  };

  const toggleOrder = (orderId) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  };

  const toggleAllOrders = () => {
    const all = scheduleData?.orders || [];
    setSelectedOrders(all.length === selectedOrders.size ? new Set() : new Set(all.map(o => o.order_id)));
  };

  const handleWhatIfResult = (result) => {
    setWhatIfResult(result);
    setShowWhatIfGantt(true);
  };

  const activeGanttTasks = showWhatIfGantt && whatIfResult
    ? whatIfResult.modified_schedule?.gantt_data?.tasks || []
    : scheduleData?.gantt_data?.tasks || [];

  return (
    <div className="space-y-6" data-testid="scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Production Scheduling
          </h1>
          <p className="text-muted-foreground mt-1">Generate schedules, analyze scenarios, and create production orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={optimizeFor} onValueChange={setOptimizeFor}>
            <SelectTrigger className="w-[180px]" data-testid="optimize-select"><SelectValue placeholder="Optimize for..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="on_time">On-Time Delivery</SelectItem>
              <SelectItem value="utilization">Max Utilization</SelectItem>
              <SelectItem value="setup_reduction">Min Setup Time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={generateSchedule} disabled={loading} data-testid="generate-schedule-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Generate Schedule
          </Button>
          {scheduleData && (
            <Button variant="outline" onClick={() => downloadExcel("/export/schedule", "schedule.xlsx", { method: "POST" })} data-testid="export-schedule-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Export Excel
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <ScheduleSummaryCards scheduleData={scheduleData} />

      {/* Main Content */}
      {scheduleData ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card className="card-industrial">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle>Schedule Overview</CardTitle>
                    {showWhatIfGantt && whatIfResult && <Badge className="bg-amber-500/10 text-amber-600 border-amber-200">What-If View</Badge>}
                  </div>
                  <div className="flex items-center gap-3">
                    {whatIfResult && (
                      <Button variant="outline" size="sm" onClick={() => setShowWhatIfGantt(!showWhatIfGantt)} data-testid="toggle-whatif-gantt">
                        {showWhatIfGantt ? <EyeOff className="w-3 h-3 mr-1.5" /> : <Eye className="w-3 h-3 mr-1.5" />}
                        {showWhatIfGantt ? "Show Base" : "Show What-If"}
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Critical Path</span>
                      <Switch checked={showCriticalPath} onCheckedChange={setShowCriticalPath} data-testid="critical-path-toggle" />
                    </div>
                    <Select value={viewMode} onValueChange={setViewMode}>
                      <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Day">Day</SelectItem>
                        <SelectItem value="Week">Week</SelectItem>
                        <SelectItem value="Month">Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="gantt" data-testid="tab-gantt"><BarChart3 className="w-4 h-4 mr-2" />Gantt Chart</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="tab-orders"><Factory className="w-4 h-4 mr-2" />Orders ({scheduleData.orders?.length || 0})</TabsTrigger>
                    <TabsTrigger value="capacity" data-testid="tab-capacity"><BarChart3 className="w-4 h-4 mr-2" />Capacity</TabsTrigger>
                  </TabsList>
                  <TabsContent value="gantt" className="mt-4">
                    {activeGanttTasks.length > 0 ? (
                      <EnhancedGantt tasks={activeGanttTasks} viewMode={viewMode} highlightCriticalPath={showCriticalPath} />
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">No tasks to display</div>
                    )}
                  </TabsContent>
                  <TabsContent value="orders" className="mt-4">
                    <OrdersTable
                      orders={scheduleData.orders}
                      selectedOrders={selectedOrders}
                      creatingPO={creatingPO}
                      onToggleOrder={toggleOrder}
                      onToggleAll={toggleAllOrders}
                      onCreateSingle={createSinglePO}
                      onCreateBatch={createBatchPO}
                    />
                  </TabsContent>
                  <TabsContent value="capacity" className="mt-4">
                    <CapacityView resources={resources} scheduleData={scheduleData} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-1">
            <WhatIfPanel onResult={handleWhatIfResult} materials={scheduleData.orders?.map(o => o.material) || []} />
          </div>
        </div>
      ) : (
        <Card className="card-industrial">
          <CardContent className="py-16">
            <div className="text-center">
              <Calendar className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Schedule Generated</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Click &quot;Generate Schedule&quot; to create a production schedule from your open sales orders.
              </p>
              <Button onClick={generateSchedule} disabled={loading} data-testid="generate-schedule-empty-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Generate Schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Work Centers */}
      <WorkCentersGrid resources={resources} />
    </div>
  );
};

export default Scheduling;
